/**
 * Pulls a human-readable message out of anything a failed request can throw.
 *
 * Every thunk used to reject with `error.response.data.message` directly. That
 * expression assumes the request reached the server and that the server replied
 * with JSON containing `message` — so the moment the API was unreachable
 * (`error.response` is undefined on a network error, DNS failure, CORS block or
 * timeout) the rejection handler itself threw a TypeError. The thunk then
 * rejected with that TypeError instead of the real cause, `state.error` was set
 * to `undefined`, and the UI showed nothing at all.
 *
 * Nest also varies its error shape: `message` is a string for most exceptions
 * but an array of strings when class-validator rejects a DTO.
 */
export const extractApiError = (
  error: unknown,
  /**
   * Optional friendlier wording, keyed on the API's `code` field.
   *
   * The server's own `message` is written for whoever is reading a log, not for
   * whoever is looking at a form — a failed sign-in says "Invalid credentials",
   * and a rejected guard says "Unauthorized", which tells an administrator
   * nothing about what to do next.
   *
   * Keyed on `code` rather than on the message text because `code` is a stable
   * contract (src/core/errors/error-codes.ts on the API) while the message is
   * prose someone will reword eventually. Matching on prose is how a UI quietly
   * stops recognising its own errors.
   */
  messagesByCode?: Record<string, string>,
): string => {
  const e = error as {
    response?: {
      data?: { message?: string | string[]; error?: string; code?: string };
      status?: number;
    };
    code?: string;
    message?: string;
  };

  const data = e?.response?.data;

  const override = data?.code ? messagesByCode?.[data.code] : undefined;
  if (override) {
    return override;
  }

  if (data) {
    // class-validator returns every failed constraint as an array.
    if (Array.isArray(data.message)) {
      return data.message.filter(Boolean).join(', ') || 'Request failed';
    }
    if (typeof data.message === 'string' && data.message) {
      return data.message;
    }
    if (typeof data.error === 'string' && data.error) {
      return data.error;
    }
  }

  if (e?.code === 'ECONNABORTED') {
    return 'The server took too long to respond. Please try again.';
  }

  // No response at all: the request never made it to the API.
  if (!e?.response) {
    return 'Cannot reach the server. Check that the API is running and REACT_APP_API_URL is correct.';
  }

  if (e.response.status) {
    return `Request failed with status ${e.response.status}`;
  }

  return e?.message || 'Something went wrong';
};
